import { useState } from 'react';

interface FilterPanelProps {
  onSearchChange?: (search: string) => void;
  onConfidenceFilter?: (confidence: 'high' | 'medium' | 'low' | null) => void;
  onSortChange?: (sort: 'confidence' | 'date' | 'amount') => void;
  onAddTransaction?: () => void;
  onDateRangeChange?: (start?: string, end?: string) => void;
  onAmountRangeChange?: (min?: number, max?: number) => void;
  onAccountChange?: (account?: string) => void;
}

export const FilterPanel = ({ 
  onSearchChange, 
  onConfidenceFilter, 
  onSortChange,
  onAddTransaction,
  onDateRangeChange,
  onAmountRangeChange,
  onAccountChange,
}: FilterPanelProps) => {
  const [search, setSearch] = useState('');
  const [activeConfidence, setActiveConfidence] = useState<'high' | 'medium' | 'low' | null>(null);
  const [sortBy, setSortBy] = useState<'confidence' | 'date' | 'amount'>('confidence');
  const [showFilters, setShowFilters] = useState(false);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [minAmount, setMinAmount] = useState<string>('');
  const [maxAmount, setMaxAmount] = useState<string>('');
  const [account, setAccount] = useState<string>('');
  const [activeFilterCount, setActiveFilterCount] = useState(0);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    onSearchChange?.(value);
  };

  const handleConfidenceClick = (confidence: 'high' | 'medium' | 'low') => {
    const newValue = activeConfidence === confidence ? null : confidence;
    setActiveConfidence(newValue);
    onConfidenceFilter?.(newValue);
  };

  const handleSortClick = () => {
    const sortOptions: Array<'confidence' | 'date' | 'amount'> = ['confidence', 'date', 'amount'];
    const currentIndex = sortOptions.indexOf(sortBy);
    const nextSort = sortOptions[(currentIndex + 1) % sortOptions.length];
    setSortBy(nextSort);
    onSortChange?.(nextSort);
  };

  const getSortLabel = () => {
    switch (sortBy) {
      case 'confidence': return 'Confidence Score';
      case 'date': return 'Date';
      case 'amount': return 'Amount';
    }
  };

  const updateActiveFilterCount = () => {
    let count = 0;
    if (startDate || endDate) count++;
    if (minAmount || maxAmount) count++;
    if (account) count++;
    setActiveFilterCount(count);
  };

  const handleStartDateChange = (value: string) => {
    setStartDate(value);
    onDateRangeChange?.(value || undefined, endDate || undefined);
    setTimeout(updateActiveFilterCount, 0);
  };

  const handleEndDateChange = (value: string) => {
    setEndDate(value);
    onDateRangeChange?.(startDate || undefined, value || undefined);
    setTimeout(updateActiveFilterCount, 0);
  };

  const handleMinAmountChange = (value: string) => {
    setMinAmount(value);
    const minNum = value === '' ? undefined : Number(value);
    const maxNum = maxAmount === '' ? undefined : Number(maxAmount);
    onAmountRangeChange?.(minNum, maxNum);
    setTimeout(updateActiveFilterCount, 0);
  };

  const handleMaxAmountChange = (value: string) => {
    setMaxAmount(value);
    const minNum = minAmount === '' ? undefined : Number(minAmount);
    const maxNum = value === '' ? undefined : Number(value);
    onAmountRangeChange?.(minNum, maxNum);
    setTimeout(updateActiveFilterCount, 0);
  };

  const handleAccountFilterChange = (value: string) => {
    setAccount(value);
    onAccountChange?.(value || undefined);
    setTimeout(updateActiveFilterCount, 0);
  };

  const handleReset = () => {
    setStartDate('');
    setEndDate('');
    setMinAmount('');
    setMaxAmount('');
    setAccount('');
    onDateRangeChange?.(undefined, undefined);
    onAmountRangeChange?.(undefined, undefined);
    onAccountChange?.(undefined);
    setActiveFilterCount(0);
  };

  return (
    <section className="bg-white p-4 rounded-2xl border border-brand-border mb-6 shadow-sm">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative w-64">
            <input 
              type="text" 
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search counterparty, description..." 
              className="w-full h-10 pl-10 pr-4 bg-brand-secondary/50 rounded-xl text-sm text-brand-text-primary placeholder:text-brand-text-secondary/60 focus:outline-none focus:ring-2 focus:ring-brand-accent focus:bg-white transition-all"
            />
            <i className="fa-solid fa-magnifying-glass text-brand-text-secondary absolute left-3 top-1/2 -translate-y-1/2 text-sm"></i>
          </div>
          <button 
            onClick={() => setShowFilters(!showFilters)}
            className={`h-10 px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2 transition-all ${
              showFilters || activeFilterCount > 0
                ? 'bg-brand-accent text-white shadow-md'
                : 'bg-brand-secondary/50 text-brand-primary hover:bg-brand-secondary'
            }`}
          >
            <i className="fa-solid fa-sliders"></i> 
            Filters
            {activeFilterCount > 0 && (
              <span className="ml-1 px-1.5 py-0.5 bg-white/20 rounded-full text-xs font-bold">
                {activeFilterCount}
              </span>
            )}
          </button>
          <div className="h-8 w-px bg-brand-border/50"></div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-brand-text-secondary font-medium">Confidence:</span>
            <button 
              onClick={() => handleConfidenceClick('high')}
              className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-all ${
                activeConfidence === 'high' 
                  ? 'bg-brand-success text-white shadow-md scale-105' 
                  : 'bg-brand-success-bg text-brand-success hover:bg-brand-success-bg/80 border border-brand-border'
              }`}
            >
              <i className="fa-solid fa-circle-check mr-1"></i>High
            </button>
            <button 
              onClick={() => handleConfidenceClick('medium')}
              className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-all ${
                activeConfidence === 'medium' 
                  ? 'bg-brand-warning text-white shadow-md scale-105' 
                  : 'bg-brand-warning-bg text-brand-warning hover:bg-brand-warning-bg/80 border border-brand-border'
              }`}
            >
              <i className="fa-solid fa-circle-half-stroke mr-1"></i>Medium
            </button>
            <button 
              onClick={() => handleConfidenceClick('low')}
              className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-all ${
                activeConfidence === 'low' 
                  ? 'bg-brand-danger text-white shadow-md scale-105' 
                  : 'bg-brand-danger-bg text-brand-danger hover:bg-brand-danger-bg/80 border border-brand-border'
              }`}
            >
              <i className="fa-solid fa-circle-exclamation mr-1"></i>Low
            </button>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-brand-text-secondary font-medium">Sort:</span>
          <button 
            onClick={handleSortClick}
            className="h-10 px-4 py-2 bg-brand-secondary/50 rounded-xl text-sm text-brand-primary font-medium flex items-center gap-2 hover:bg-brand-secondary transition-all"
          >
            <i className="fa-solid fa-arrow-down-short-wide"></i>
            {getSortLabel()}
          </button>
          <button 
            onClick={() => onAddTransaction?.()}
            className="h-10 px-4 py-2 bg-brand-primary text-white rounded-xl text-sm font-medium flex items-center gap-2 hover:bg-brand-primary/90 shadow-md hover:shadow-lg transition-all"
          >
            <i className="fa-solid fa-plus"></i> Add Transaction
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="mt-4 pt-4 border-t border-brand-border">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 animate-fade-in">
            <div className="space-y-2">
              <label className="text-xs font-medium text-brand-text-secondary flex items-center gap-1">
                <i className="fa-solid fa-calendar-days text-brand-accent"></i>
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => handleStartDateChange(e.target.value)}
                className="w-full h-10 px-3 bg-brand-secondary/50 rounded-lg text-sm text-brand-text-primary focus:outline-none focus:ring-2 focus:ring-brand-accent focus:bg-white transition-all"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-medium text-brand-text-secondary flex items-center gap-1">
                <i className="fa-solid fa-calendar-days text-brand-accent"></i>
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => handleEndDateChange(e.target.value)}
                className="w-full h-10 px-3 bg-brand-secondary/50 rounded-lg text-sm text-brand-text-primary focus:outline-none focus:ring-2 focus:ring-brand-accent focus:bg-white transition-all"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-medium text-brand-text-secondary flex items-center gap-1">
                <i className="fa-solid fa-coins text-brand-accent"></i>
                Min Amount (CHF)
              </label>
              <input
                type="number"
                inputMode="decimal"
                value={minAmount}
                onChange={(e) => handleMinAmountChange(e.target.value)}
                className="w-full h-10 px-3 bg-brand-secondary/50 rounded-lg text-sm text-brand-text-primary focus:outline-none focus:ring-2 focus:ring-brand-accent focus:bg-white transition-all"
                placeholder="0.00"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-medium text-brand-text-secondary flex items-center gap-1">
                <i className="fa-solid fa-coins text-brand-accent"></i>
                Max Amount (CHF)
              </label>
              <input
                type="number"
                inputMode="decimal"
                value={maxAmount}
                onChange={(e) => handleMaxAmountChange(e.target.value)}
                className="w-full h-10 px-3 bg-brand-secondary/50 rounded-lg text-sm text-sm text-brand-text-primary focus:outline-none focus:ring-2 focus:ring-brand-accent focus:bg-white transition-all"
                placeholder="0.00"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-medium text-brand-text-secondary flex items-center gap-1">
                <i className="fa-solid fa-hashtag text-brand-accent"></i>
                Account Number
              </label>
              <input
                type="text"
                value={account}
                onChange={(e) => handleAccountFilterChange(e.target.value)}
                className="w-full h-10 px-3 bg-brand-secondary/50 rounded-lg text-sm text-brand-text-primary focus:outline-none focus:ring-2 focus:ring-brand-accent focus:bg-white transition-all"
                placeholder="e.g., 3200"
              />
            </div>
          </div>
          {activeFilterCount > 0 && (
            <div className="mt-4 flex justify-between items-center">
              <span className="text-sm text-brand-text-secondary">
                <i className="fa-solid fa-filter mr-2 text-brand-accent"></i>
                {activeFilterCount} active {activeFilterCount === 1 ? 'filter' : 'filters'}
              </span>
              <button
                onClick={handleReset}
                className="h-9 px-4 py-2 bg-brand-danger-bg text-brand-danger rounded-lg text-sm font-medium hover:bg-brand-danger-bg/80 transition-all flex items-center gap-2"
              >
                <i className="fa-solid fa-xmark"></i>
                Clear All Filters
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
};